import {XbrlContext, XbrlContextMap} from "@/edinet/types"
import moment from 'moment'
import $ from 'jquery'
import {textToFloat} from "@/edinet/libs/utils"

function formatNegWithBracket(v: number, cls: string) {
    const vFmt = v.toLocaleString()
    if (vFmt.charAt(0) !== '-') {
        //need the hidden span to align with negative number )
        return `<span class="${cls} pos">${vFmt}</span><span style="visibility: hidden;">)</span>`
    } else {
        //show negative number as (xxx) instead of -xxx
        return `<span class="${cls} neg">(${vFmt.substr(1)})</span>`
    }
}

function formatNegWithDelta(v: number, cls: string) {
    const vFmt = v.toLocaleString()
    if (vFmt.charAt(0) !== '-') {
        return `<span class="${cls} pos">+${vFmt}</span>`
    } else {
        return `<span class="${cls} neg">∆${vFmt.substr(1)}</span>`
    }
}

function formatPctSales(v: number) {
    const vFmt = v.toLocaleString()
    if (vFmt.charAt(0) !== '-') {
        return `<span>${vFmt}%</span>`
    } else {
        return `<span>∆${vFmt.substr(1)}%</span>`
    }
}

function formatPct(v: number, cls: string) {
    const vFmt = v.toLocaleString()
    if (v >= 0) {
        return `<span class="${cls} pos">+${vFmt}%</span>`
    } else {
        return `<span class="${cls} neg">∆${vFmt.substr(1)}%</span>`
    }
}

function calcPercent(values: number[]) {
    try {
        return Math.round(100 * (values[1] - values[0]) / Math.abs(values[0]))
    } catch (e) {
        console.error(e)
        return 0
    }
}


const htmlControl = `<div>
    <label>scale</label>
    <select id="select-scale" >
            <option value="3">ths</option>                   
            <option value="6">mil</option>                                      
            <option value="9">bn</option>                                      
    </select>
    
    <input type="checkbox" id="checkbox-ix">
    <label>ix</label>
    
    <input type="checkbox" id="checkbox-highlight-change">
    <label>highlight</label>
    
    <input type="checkbox" id="checkbox-diff">
    <label>∆</label>
    <input type="checkbox" id="checkbox-diff-pct">
    <label>∆%</label>
</div>`

const htmlWrapper = `<div class="smarttable-container"></div>`

interface Row {
    type: string
    label: string
    name?: string
    scale?: number
    values?: number[]
    ctxRefs?: string[]

    //derived
    diff?: (number | null)[]
    pct?: (number | null)[]
    cls?: (string | null)[]
}

type Table = Row[]

//context helper
function isPeriodBased(ctx: XbrlContext): boolean {
    return ctx.periodstart !== ''
}

function ctxDuration(ctx: XbrlContext): number {
    return Math.round(moment(ctx.periodend).diff(moment(ctx.periodstart), 'months', true))
}

function isPeriodAscending(ctx1: XbrlContext, ctx2: XbrlContext): boolean {
    return moment(ctx1.periodend).isBefore(moment(ctx2.periodend))
}
function isInstantAscending(ctx1: XbrlContext, ctx2: XbrlContext): boolean {
    return moment(ctx1.instant).isBefore(moment(ctx2.instant))
}

export interface SmartTableOptions {
    checkHighlight?: boolean
    checkIx?: boolean
    checkDiff?: boolean
    checkDiffPct?: boolean
    checkPctSales?: boolean
}

export class SmartTableBlock {
    html: HTMLElement
    $html: JQuery
    contextMap: XbrlContextMap
    exploreUrl?: string

    salesIxNames?: string[]
    options?: SmartTableOptions

    init: boolean
    $control: JQuery
    tables: Table[]

    /**
     *
     * @param html
     * @param exploreUrl
     * @param salesIxNames optional - ix attribute name of sales. if provide, calculate %sales for all rows
     */
    constructor(html: HTMLElement, contextMap: XbrlContextMap, exploreUrl?: string, salesIxNames?: string[], options?: SmartTableOptions) {
        this.html = html
        this.$html = $(html)
        this.exploreUrl = exploreUrl
        this.contextMap = contextMap
        this.salesIxNames = salesIxNames
        this.options = options

        this.init = false
        this.$control = $(htmlControl)
        this.tables = []
    }

    setupHtml() {
        if (this.salesIxNames) {
            this.$control.append(`
    <input type="checkbox" id="checkbox-pct-sales">
    <label>%sales</label>`)
        }
        if (this.exploreUrl) {
            this.$control.append(`<button class="open" onClick="window.open('${this.exploreUrl}');">Open</button>`)
        }

        //attach event listeners
        this.$control.find('#select-scale').change(() => this.updateState())
        this.$control.find('#checkbox-ix').change((e) => {
            this.renderIfNotYetDone()
            const spans = this.$html.find('.ix-name')
            if ((e.target as HTMLInputElement).checked) {
                spans.show()
            } else {
                spans.hide()
            }
        })
        this.$control.find('#checkbox-diff').change((e) => {
            this.renderIfNotYetDone()
            const tds = this.$html.find('.diff')
            if ((e.target as HTMLInputElement).checked) {
                tds.show()
            } else {
                tds.hide()
            }
        })
        this.$control.find('#checkbox-diff-pct').change((e) => {
            this.renderIfNotYetDone()
            const tds = this.$html.find('.diff-pct')
            if ((e.target as HTMLInputElement).checked) {
                tds.show()
            } else {
                tds.hide()
            }
        })
        this.$control.find('#checkbox-pct-sales').change((e) => {
            this.renderIfNotYetDone()
            const tds = this.$html.find('.pct-sales')
            if ((e.target as HTMLInputElement).checked) {
                tds.show()
            } else {
                tds.hide()
            }
        })
        this.$control.find('#checkbox-highlight-change').change((e) => {
            this.renderIfNotYetDone()
            const hl = this.$html.find('.big-increase,.big-decrease')
            if ((e.target as HTMLInputElement).checked) {
                hl.addClass('on')
            } else {
                hl.removeClass('on')
            }
        })

        this.$html
            .wrapAll(htmlWrapper)

        this.$html.find('table:first')
            .parent()
            .prepend(this.$control)

        this.$html.find('tr').click(function() {
            $(this).toggleClass('selected')
        })

        //set default check boxes
        const options = this.options
        if (options) {
            if (options.checkHighlight) this.$control.find('#checkbox-highlight-change').prop('checked', options.checkHighlight)
            if (options.checkIx) this.$control.find('#checkbox-ix').prop('checked', options.checkIx)
            if (options.checkDiff) this.$control.find('#checkbox-diff').prop('checked', options.checkDiff)
            if (options.checkDiffPct) this.$control.find('#checkbox-diff-pct').prop('checked', options.checkDiffPct)
            if (options.checkPctSales) this.$control.find('#checkbox-pct-sales').prop('checked', options.checkPctSales)
        }
    }

    setup() {
        // console.log(this.$html.parent('.smarttable').length)
        if (this.$html.parents('.smarttable-container').length === 0) {
            this.setupHtml()
            // this.updateState() //comment this out to prevent rendering by default
        } else {
            console.debug('smarttable already setup for %o', this.html)
        }
    }

    cleanTables() {
        this.$html.find('table').each((i, table) => {
            const $table = $(table)
            try { //need to remove fixed layout if we add column
                if ($table.css('table-layout') === 'fixed') {
                    $table.css('table-layout', '')
                    $table.css('width', '')
                }
                $table.find('td').each((i, td) => {
                    const $td = $(td)
                    if ($td.css('background-color')) {
                        $td.css('background-color', '')
                    }
                })
                $table.find('colgroup').remove()
            } catch (e) {
                console.error('error trying to clean table: %o', e)
            }
        })
    }

    prepareTables() {
        //set default scale
        // console.log(this.html)
        this.tables = this.parseTables()
        const scale = this.tables[0].find(x => x.type === 'nonfraction')?.scale
        if (scale) {
            this.$control.find('#select-scale').val(scale.toString())
        }
    }

    assignClasses() {
        this.$html.parent().addClass('smarttable')
        this.$html.find('td:has(ix\\:nonfraction)').each((i, td) => {
            $(td).addClass('nonfraction')
        })
    }

    renderIfNotYetDone() {
        if (!this.init) {
            this.updateState()
        }
    }

    updateState() {
        if (!this.init) {
            this.prepareTables()
            this.cleanTables()
            this.assignClasses()
            this.init = true
        }

        this.render()

        this.$control.find('#checkbox-ix,#checkbox-diff,#checkbox-diff-pct,#checkbox-highlight-change,#checkbox-pct-sales').trigger('change')
    }

    render() {
        this.$html.find('table').each((i, table) => {
            this.renderTable(table, this.tables[i])
        })
    }

    /**
     * calculate values to extend to the table
     * need special handling on which column to compare and the order of the year
     *
     * 1. group column by same duration (i.e. quarterly 3m or ytd 9m)
     * 2. check column order within the group
     * 3. do the diff, pc and assign class for the columns within the group
     * @param rows
     * @param ctxMap
     */
    ctxSequence(rows: Table, ctxMap: XbrlContextMap) {
        function calculateDerivedFields(isAscending: boolean, r: Row, i1: number, i2: number) {
            if (r.values && r.values.length > 1 && r.diff && r.pct && r.cls) {
                if (isAscending) {
                    //order incrementally i.e. 2019 -> 2020
                    const vOld = r.values[i1]
                    const vNew = r.values[i2]

                    const diff = vNew - vOld
                    const pct = calcPercent([vOld, vNew])
                    // r.diff[i1] = null
                    r.diff[i2] = diff

                    // r.pct[i1] = null
                    r.pct[i2] = pct

                    // r.cls[i1] = null
                    r.cls[i2] = (pct > 20) ? 'big-increase' : (pct < -20) ? 'big-decrease' : null

                } else {
                    //2020 -> 2019
                    const vNew = r.values[i1]
                    const vOld = r.values[i2]

                    const diff = vNew - vOld
                    const pct = calcPercent([vOld, vNew])
                    // r.diff[i1] = null
                    r.diff[i2] = diff

                    // r.pct[i1] = null
                    r.pct[i2] = pct

                    //note: add cls to new ctx (i1)
                    r.cls[i1] = (pct > 20) ? 'big-increase' : (pct < -20) ? 'big-decrease' : null
                    // r.cls[i2] = null
                }
            }
        }

        for (const r of rows) {
            if (r.type === 'nonfraction' && r.values && r.ctxRefs && r.values.length > 1) {
                r.diff = []
                r.pct = []
                r.cls = []
                //starts from 2nd column
                for (let i = 1; i < r.values.length; i++) {
                    const i1 = i - 1
                    const i2 = i
                    const ctx1 = ctxMap[r.ctxRefs[i1]]
                    const ctx2 = ctxMap[r.ctxRefs[i2]]

                    if (isPeriodBased(ctx1)) {
                        const d1 = ctxDuration(ctx1)
                        const d2 = ctxDuration(ctx2)
                        // console.log('duration %o vs %o', d1, d2)

                        if (d1 === d2) {
                            //since we don't know the sorting order of columns - i.e. 2019 -> 2020 or 2020 -> 2019
                            //calculate derived values for every same duration pair and assign values based on appearance order
                            calculateDerivedFields(isPeriodAscending(ctx1, ctx2), r, i1, i2)
                        } else {
                            //different duration - do nothing
                        }
                    } else {
                        //instant based - same as above but no need to compare duration
                        calculateDerivedFields(isInstantAscending(ctx1, ctx2), r, i1, i2)
                    }
                }
            }
        }
    }

    renderTable(table: HTMLTableElement, rows: Table) {
        const scale = Math.pow(10, parseInt(this.$control.find('#select-scale').val() as string))
        //start rendering from clean state
        this.$html.find('.extra').remove()
        this.ctxSequence(rows, this.contextMap)

        //for pnl
        let salesRow: Row | undefined
        if (this.salesIxNames) {
            const m = rows.filter(x => x.name && this.salesIxNames?.includes(x.name))
            if (m.length > 0) {
                salesRow = m[m.length - 1] //last matched row (in case there is total)
            }
        }

        const $table = $(table)

        //iterate row
        $table.find('tr').each((i, tr) => {
            const $tr = $(tr)
            //modify existing columns
            const d = rows[i]

            let nf = 0
            //iterate column
            $tr.find('td').each((j, td) => {
                const $td = $(td)

                //nonfraction row
                if (d.type === 'nonfraction') {
                    if (j === 0) {
                        //first col
                        $td.html($td.html() + `<span class='extra ix-name'>${d.name}</span>`)
                    }
                    //nonfraction column
                    if ($td.hasClass('nonfraction') && d.values) {
                        const v = d.values[nf]
                        //do not scale small value (i.e. EPS)
                        const vScaled = (Math.abs(v) > 10) ? Math.round(v / scale) : v
                        let cls = 'value'
                        if (d.cls && d.cls[nf]) {
                            cls += ' ' + d.cls[nf]
                        }
                        $td.html(formatNegWithBracket(vScaled, cls))

                        if (d.pct) {
                            const pct = d.pct[nf]
                            if (pct) {
                                const v = formatPct(pct, 'pct')
                                const tdNew = `<td class="value">${v}</td>`
                                $td.after($(tdNew).addClass(['extra', 'diff-pct'])) //add class so that we can remove it later
                            }
                        }
                        if (d.diff) {
                            const diff = d.diff[nf]
                            if (diff) {
                                //do not scale small value (i.e. EPS)
                                const diffScaled = (diff > 10 || diff < -10) ? Math.round(diff / scale) : diff
                                // td = `<td class="value">${formatNegWithDelta(diffScaled, '')} ${formatPct(pct, 'pct')}</td>`
                                const v = formatNegWithDelta(diffScaled, '')
                                const tdNew = `<td class="value">${v}</td>`
                                $td.after($(tdNew).addClass(['extra', 'diff'])) //add class so that we can remove it later
                            }
                        }
                        if (salesRow && salesRow.values) {
                            const pctSales = Math.round(100 * d.values[nf] / salesRow.values[nf])
                            const tdNew = `<td class="value"> ${formatPctSales(pctSales)}</td>`
                            $td.after($(tdNew).addClass(['extra', 'pct-sales']))
                        }
                        nf++
                    }
                }
            })
        })

        this.addColPaddings($table)
    }

    addColPaddings($table: JQuery) {
        //analyze and add <td></td> padding
        function makeEmptyTd($model: JQuery) {
            const cls = $model.attr('class')
            // console.log('pad: ' + cls)
            const $newTd = $('<td></td>')
            return cls ? $newTd.addClass(cls).removeClass('value') : null
        }

        function getSpan($el: JQuery): number {
            return parseInt($el.attr('colspan') || '1')
        }

        //padding will try to fill column according to this row
        const modelRow = $table.find('tr:has(td.nonfraction)').first().find('td')
        // console.log(modelRow.get())
        // console.log(modelRow.length)

        //add padding to try to keep spancount consistent with model row
        $table.find('tr').each((i, tr) => {
            const $tr = $(tr)
            const tds = $tr.find('td')

            //only padding in text rows
            if ($tr.find('td.nonfraction').length === 0) {
                // console.log(tr)
                // console.log(tds.length)
                //non-nonfraction row
                let spancount = 0
                let mspancount = 0
                let i = 0
                let mi = 0
                while (i < tds.length) {
                    let $td = $(tds[i])
                    // console.log('td: %o vs %o', tds[i], modelRow[mi])
                    let $model = $(modelRow[mi])
                    spancount += getSpan($td)
                    mspancount += getSpan($model)
                    if ($model.hasClass('extra')) {
                        const $newTd = makeEmptyTd($model)
                        if ($newTd) {
                            $td.after($newTd)
                            spancount += 1 //increase spancount since we just add new td
                        }
                    }
                    while (spancount < mspancount && i < tds.length) {
                        i++
                        $td = $(tds[i])
                        spancount += getSpan($td)
                        // console.log('walk td: %o', tds[i])
                    }
                    while (spancount > mspancount && i < tds.length) {
                        mi++
                        $model = $(modelRow[mi])
                        mspancount += getSpan($model)
                        // console.log('walk m: %o', modelRow[mi])
                        if ($model.hasClass('extra')) {
                            const $newTd = makeEmptyTd($model)
                            if ($newTd) {
                                $td.after($newTd)
                                spancount += 1 //increase spancount since we just add new td
                            }
                        }
                    }
                    // console.log('cspan: %o vs %o', spancount, mspancount)
                    i++
                    mi++
                }
            }
        })
    }

    parseTables(): Table[] {
        const tables: Table[] = []
        this.$html.find('table').each((i, t) => {

            const table = this.parseTable(t)
            // console.log(table)
            // table.slice(4, 7).forEach(console.log)
            tables.push(table)
        })
        return tables
    }

    parseNonfractionValue(nf: HTMLElement, scale: number) {
        const $nf = $(nf)
        const sign = $nf.attr('sign')
        const signum = (sign && sign === '-') ? -1 : 1
        const scaleMultiplier = Math.pow(10, scale)
        return signum * scaleMultiplier * textToFloat($nf.text().trim())
    }

    parseTable(table: HTMLTableElement): Table {
        const rows: Table = []
        let firstLabel = false
        $(table).find('tr').each((i, tr) => {
            const $tr = $(tr)

            //find header row from the first row with rowLabel
            const rowLabel = $tr.find('td:first').text().trim()
            if (!firstLabel && rowLabel.length > 0) {
                rows[i - 1].type = 'header'
                firstLabel = true
            }

            const nfs = $tr.find('ix\\:nonfraction')
            if (nfs.length === 0) {
                rows.push({
                    type: 'text',
                    label: rowLabel
                })
            } else {
                const $nf = $(nfs[0])
                const sc = $tr.find('ix\\:nonfraction[scale]')
                // eslint-disable-next-line  @typescript-eslint/no-non-null-assertion
                const scale = (sc.length > 0) ? parseInt(sc.first().attr('scale')!) : 1
                const values = nfs.map((i, x) => this.parseNonfractionValue(x, scale)).get()
                const ctxRefs = nfs.map((i, x) => $(x).attr('contextref')).get()

                const res = {
                    type: 'nonfraction',
                    label: rowLabel,
                    name: $nf.attr('name'),
                    scale: scale,
                    values: values,
                    ctxRefs: ctxRefs
                }
                rows.push(res)
            }
        })
        return rows
    }
}